import {
  Card,
  CardContent,
  Container,
  Typography,
  Box,
  Alert,
  Skeleton,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useEffect, useState } from "react";
import { useCurrentOrg } from "~/providers/GraphqlProvider";
import KPIFilters from "./components/KPIFilters";
import { subDays, differenceInDays, differenceInMonths, differenceInYears, startOfToday, endOfToday } from "date-fns";
import { ProviderUtilizationChart } from "./components/ProviderUtilizationChart";
import { CancelationRateChart } from "./components/CancelationRateChart";
import { VisitVolumeChart } from "./components/VisitVolumeChart";
import ProgressCard from "./components/ProgressCard";
import BarChart from "../common/charts/BarChart";
import GroupedBarChart from "../common/charts/GroupedBarChart";
import { StatusBgMap, VisitTypeColors } from "~/utils/statuses";
import { useGetKpiVisitPerShiftQuery, useGetKpiVisitTypesQuery, useKpiSummaryQuery } from "~/graphql/__generated__/graphql";
import SkeletonBarChart from "../common/charts/SkeletonBarChart";
import { HomeHealthPlannedVsActualWidget } from "~/features/home-health-demo";
import _ from "lodash";
import { rankDisplayName, rankToHexColor } from "~/utils/scheduleOptions";
import useDefaultServiceLines from "~/hooks/useDefaultServiceLines";

export interface KPIFiltersType {
  serviceLineId?: number | undefined;
  providerId?: number | undefined;
  dateRange: { from: Date; to: Date };
}

export type KPIFilterKey = keyof KPIFiltersType;

export type KPIFilterValue<K extends KPIFilterKey> = KPIFiltersType[K];

const KPIDashboardScreen: React.FC = () => {
  const currentOrganization = useCurrentOrg()

  const serviceLineId: number | undefined = useDefaultServiceLines()[0]
  const [filters, setFilters] = useState<KPIFiltersType>({
    serviceLineId,
    providerId: undefined,
    dateRange: {
      from: subDays(startOfToday(), 7),
      to: endOfToday(),
    }
  });

  const queryInput = {
    startTime: filters.dateRange?.from?.toISOString() || '',
    endTime: filters.dateRange?.to?.toISOString() || '',
    serviceLineId: filters.serviceLineId ?? null,
    providerId: filters.providerId ?? null,
  };

  const { data: visitTypeVolumeData, loading: loadingVisitTypeVolume, error: errorVisitTypeVolume } = useGetKpiVisitTypesQuery(
    {
      variables: {
        input: queryInput
      },
    },
  );

  const { data: kpiSummaryData, loading: loadingSummaryData, error: errorSummaryData } = useKpiSummaryQuery(
    {
      variables: {
        input: queryInput
      }
    });

  const { data: visitsPer8HShiftData, loading: loadingVisitsPerShift, error: errorVisitsPerShifts } = useGetKpiVisitPerShiftQuery(
    {
      variables: {
        input: queryInput
      },
    },
  );

  const kpiSummary = kpiSummaryData?.kpiSummary;

  const visitData = visitsPer8HShiftData?.kpiVisitsPerShift?.visitsPerShift?.map((visitData) => {
    return {
      date: visitData?.date ?? '',
      total: visitData?.visitsPerShift ?? 0,
    };
  })

  const handleFilterChange = <K extends KPIFilterKey>(
    filter: K,
    value: KPIFilterValue<K>
  ) => {
    setFilters((prev: KPIFiltersType) => ({
      ...prev,
      [filter]: value,
    }));
  };

  const [dateRange, setDateRange] = useState<"daily" | "weekly" | "monthly" | "yearly">("daily");

  useEffect(() => {
    const from = filters.dateRange.from;
    const to = filters.dateRange.to;

    const dayDifference = differenceInDays(to, from);
    const monthDifference = differenceInMonths(to, from);
    const yearDifference = differenceInYears(to, from);

    if (yearDifference >= 1) {
      setDateRange("yearly");
    } else if (monthDifference >= 1) {
      setDateRange("monthly");
    } else if (dayDifference > 7) {
      setDateRange("weekly");
    } else {
      setDateRange("daily");
    }
  }, [filters.dateRange.from, filters.dateRange.to]);

  const visitStackedBarChartData = visitTypeVolumeData?.kpiVisitTypes.visitVolumeByDate;

  return (
    <>
      <KPIFilters
        title={`${currentOrganization?.name}`}
        filters={filters}
        onFilterChange={handleFilterChange}
      />
      <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={2}>
          <Grid size={{xs: 12, md: 8}}>
            <Grid container spacing={2} wrap="nowrap">
              <Grid size={6}>
                <ProviderUtilizationChart
                  startTime={filters.dateRange.from}
                  endTime={filters.dateRange.to}
                  providerId={filters.providerId}
                  serviceLineId={filters.serviceLineId}
                />
              </Grid>
              <Grid size={6}>
                <CancelationRateChart
                  startTime={filters.dateRange.from ? filters.dateRange.from : undefined}
                  endTime={filters.dateRange.to ? filters.dateRange.to : undefined}
                  providerId={filters.providerId}
                  serviceLineId={filters.serviceLineId}
                />
              </Grid>
            </Grid>

            <Grid size={12} sx={{ mt: 2 }}>
              <Card>
                <CardContent>
                  <Typography sx={{ mb: 3 }} fontWeight={500}>Visits per 8 hour shift</Typography>

                  {loadingVisitsPerShift ? <Box sx={{ maxWidth: '80%' }}><SkeletonBarChart /></Box> : (
                    errorVisitsPerShifts ? (
                      <Alert severity="error">
                        Error loading visits per 8 hour shift chart
                      </Alert>
                    ) : (
                      !visitData || !visitData?.length ? (
                        <>
                          <Alert severity="warning">
                            No Data
                          </Alert>
                        </>
                      ) : (
                        visitData && visitData?.length ? (
                          <BarChart
                            chartTitle="Visits per 8 hour shift"
                            xAxisLabel=""
                            yAxisLabel="Total Visits"
                            data={visitData}
                          />
                        ) : null
                      )
                    )
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid size={12} sx={{ mt: 2 }}>
              <Card>
                <CardContent>
                  <Typography sx={{ mb: 3 }} fontWeight={500}>Visits by Visit Type</Typography>

                  {loadingVisitTypeVolume ? <Box sx={{ maxWidth: '80%' }}><SkeletonBarChart /></Box> : (
                    errorVisitTypeVolume ? (
                      <Alert severity="error">
                        Error loading visit type data
                      </Alert>
                    ) : (
                      !visitStackedBarChartData || !visitStackedBarChartData?.length ? (
                        <>
                          <Alert severity="warning">
                            No Data
                          </Alert>
                        </>
                      ) : (
                        visitStackedBarChartData && visitStackedBarChartData?.length ? (
                          <GroupedBarChart
                            data={visitStackedBarChartData}
                            chartTitle=""
                            yAxisLabel="Total Visits"
                            colors={VisitTypeColors}
                            startDate={filters.dateRange.from}
                            endDate={filters.dateRange.to}
                            dateRange={dateRange}
                          />
                        ) : null
                      )
                    )
                  )}
                </CardContent>
              </Card>
            </Grid>

            <HomeHealthPlannedVsActualWidget />
          </Grid>

          {/* Right Sidebar */}
          <Grid size={{xs: 12, md: 4}}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <VisitVolumeChart
                data={visitTypeVolumeData}
                loading={loadingVisitTypeVolume}
              /> 
              <Card>
                <CardContent>
                  <Typography fontWeight={500} sx={{ mb: 2 }}>Total Visits: {kpiSummary?.totalVisits ?? 0}</Typography>
                  {!loadingSummaryData && errorSummaryData ? (
                    <Alert severity="error">
                      Error getting summary data.
                    </Alert>
                  ) : loadingSummaryData && (
                    <Box sx={{ marginTop: 2 }}>
                      <Skeleton variant="rectangular" width="100%" height={5} />
                      <Skeleton variant="text" sx={{ marginTop: 1 }} />
                    </Box>
                  )}

                  {!errorSummaryData && kpiSummary?.totalVisits && kpiSummary?.visitsByStatus ? Object.entries(kpiSummary?.visitsByStatus).map(([key, value]) => {
                    if (value === 0 || !kpiSummary?.totalVisits || key === '__typename' || !value) return null;

                    const percentage = Math.round((Number(value) / kpiSummary?.totalVisits) * 100);
                    const title = `${_.startCase(key)}: ${value}`;
                    const color = StatusBgMap[key];

                    return <ProgressCard key={key} title={title} percentage={percentage} color={color} />;
                  }): null}
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography fontWeight={500} sx={{ mb: 2 }}>Scheduled Options</Typography>
                  {errorSummaryData ? (
                    <Alert severity="error">
                      Error getting summary data.
                    </Alert>
                  ) : loadingSummaryData && (
                    <Box sx={{ marginTop: 2 }}>
                      <Skeleton variant="rectangular" width="100%" height={5} />
                      <Skeleton variant="text" sx={{ marginTop: 1 }} />
                    </Box>
                  )}
                  
                  {!errorSummaryData &&
                    Object.entries(kpiSummary?.rankCategoryCounts ?? []).map(([key, value]) => {
                      if (!kpiSummary?.totalVisits || !key || key === '__typename') return null;
                      const percentage = Math.round((Number(value) / kpiSummary?.totalVisits) * 100);
                      const color = rankToHexColor(key);

                      if (!color) return null;

                      const title = `${rankDisplayName(key)}: ${value}`;
                      return (
                        <ProgressCard key={key} title={title} percentage={percentage} color={color} />
                      );
                    })
                  }
                </CardContent>
              </Card>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default KPIDashboardScreen;
